import { ApiService, ResponseData } from './api'

export interface CommModuleDescriptor {
	key: string
	title: string
	description?: string | null
	default_operation_key?: string
}

export interface CommContextData {
	id: string
	instrument_key: string
	entity_type: string
	entity_id: number | string
	title: string
	description?: string | null
	channel_kind_key?: string
	provider_key?: string
	module_key?: string
	default_module_key: string
	modules: CommModuleDescriptor[]
	status_key?: string
	runtime_sync_status_key?: string | null
	runtime_sync_error_text?: string | null
	updated_at?: string | null
}

export interface CommPlaceData {
	key: string
	title: string
	description?: string | null
	context_count?: number
}

export interface CommContextsResult {
	places: CommPlaceData[]
	contexts: CommContextData[]
}

export interface CommMessageSource {
	instrument_key: string
	entity_type: string
	entity_id: string | number
	storage_key?: string
}

export interface CommMessageData {
	id: string
	space_id?: number | string
	context?: Record<string, unknown>
	source?: CommMessageSource
	direction_key: string
	status_key: string
	title?: string | null
	body_text?: string | null
	error_text?: string | null
	participant_key?: string | null
	participant_title?: string | null
	meta?: Record<string, unknown>
	payload?: Record<string, unknown>
	occurred_at?: string | null
}

export interface CommConversationResult {
	context: CommContextData
	module: CommModuleDescriptor
	messages: CommMessageData[]
	raw_count?: number
	limit?: number
	has_more?: boolean
	oldest_cursor?: string | null
	newest_cursor?: string | null
	refreshed_at?: string
}

export interface CommSendPayload {
	module_key: string
	input: Record<string, unknown>
}

export interface CommSendResult {
	context: CommContextData
	module: CommModuleDescriptor
	operation_key: string
	message?: CommMessageData | null
	result?: Record<string, unknown>
}

export interface CommConversationOptions {
	limit?: number
	beforeCursor?: string | null
}

export class CommService {
	private cpDomain: string

	constructor(private apiService: ApiService) {
		this.cpDomain = this.apiService.getConfig().cp_domain
	}

	async getContexts(spaceId: string | number): Promise<ResponseData>
	{
		return this.fetchComm(spaceId, '/conversations', 'GET')
	}

	async getConversation(
		spaceId: string | number,
		contextId: string,
		moduleKey?: string | null,
		options: CommConversationOptions = {}
	): Promise<ResponseData> {
		const params = new URLSearchParams()
		if (moduleKey)
			params.set('module_key', moduleKey)
		if (options.limit)
			params.set('limit', String(options.limit))
		if (options.beforeCursor)
			params.set('before_cursor', options.beforeCursor)

		const query = params.toString() ? `?${params.toString()}` : ''
		return this.fetchComm(spaceId, `/conversation/${encodeURIComponent(contextId)}${query}`, 'GET')
	}

	async sendConversation(
		spaceId: string | number,
		contextId: string,
		payload: CommSendPayload
	): Promise<ResponseData> {
		const query = payload.module_key ? `?module_key=${encodeURIComponent(payload.module_key)}` : ''
		return this.fetchComm(spaceId, `/conversation/${encodeURIComponent(contextId)}${query}`, 'POST', payload)
	}

	private async fetchComm(
		spaceId: string | number,
		path: string,
		method: string,
		body: unknown = undefined
	): Promise<ResponseData> {
		try {
			const response = await this.apiService.doFetch(
				this.cpDomain,
				`/${spaceId}/comm/s${path}`,
				method,
				body
			)

			return await this.apiService.getResponseJson(response)
		} catch (error) {
			return {
				success: false,
				error: error instanceof Error ? error.message : 'Ошибка запроса Comm API'
			}
		}
	}
}
